import { FC, ReactElement } from 'react'
import { Link, useLocation } from 'react-router'
import classNames from 'classnames'

import { Badge, Icon, IconName } from '@/components'
import { trans } from '@/helpers/trans'
import { useTypedSelector } from '@/redux/store'

import './ConnectedBottomTabNav.scss'

interface Tab {
    label: string
    path: string
    icon: IconName
    hasUnread: boolean
    isActive: () => boolean
}

export const ConnectedBottomTabNav: FC = (): ReactElement => {
    const { pathname } = useLocation()
    const feedUnreadCount = useTypedSelector(state => state.notificationsReducer.feedUnreadCount)
    const chatUnreadCount = useTypedSelector(state => state.notificationsReducer.chatUnreadCount)

    const homePath = `/${trans('nav.tracks.link')}`
    const communicationPath = `/${trans('nav.communication.link')}`
    const newsfeedPath = `${communicationPath}/${trans('nav.communication.newsfeed.link')}`
    const messagesPath = `${communicationPath}/${trans('nav.communication.messages.link')}`
    const shiftsPath = `/${trans('nav.shifts.link')}`
    const schedulePath = `${shiftsPath}/${trans('nav.shifts.schedule.link')}`
    const profilePath = `/${trans('nav.profile.link')}`

    const tabs: Tab[] = [
        {
            label: trans('nav.bottomNav.home'),
            path: homePath,
            icon: 'home',
            hasUnread: false,
            isActive: (): boolean => pathname.startsWith(homePath),
        },
        {
            label: trans('nav.bottomNav.feed'),
            path: newsfeedPath,
            icon: 'megaphone',
            hasUnread: feedUnreadCount > 0,
            isActive: (): boolean => pathname.startsWith(newsfeedPath),
        },
        {
            label: trans('nav.bottomNav.messages'),
            path: messagesPath,
            icon: 'chat-bubble',
            hasUnread: chatUnreadCount > 0,
            isActive: (): boolean => pathname.startsWith(messagesPath),
        },
        {
            label: trans('nav.bottomNav.schedule'),
            path: schedulePath,
            icon: 'calendar',
            hasUnread: false,
            isActive: (): boolean => pathname.startsWith(schedulePath),
        },
        {
            label: trans('nav.bottomNav.profile'),
            path: profilePath,
            icon: 'user',
            hasUnread: false,
            isActive: (): boolean => pathname.startsWith(profilePath),
        },
    ]

    return (
        <nav className="bottom-tab-nav">
            <div className="bottom-tab-nav__items">
                {tabs.map(tab => {
                    const active = tab.isActive()

                    return (
                        <Link
                            key={tab.path}
                            to={tab.path}
                            className={classNames('bottom-tab-nav__item', {
                                'bottom-tab-nav__item--active': active,
                            })}
                            aria-current={active ? 'page' : undefined}
                        >
                            <span className="bottom-tab-nav__icon-wrap">
                                <Icon name={tab.icon} className="bottom-tab-nav__icon" />
                                {tab.hasUnread && <Badge className="bottom-tab-nav__dot" />}
                            </span>
                            <span className="bottom-tab-nav__label">{tab.label}</span>
                        </Link>
                    )
                })}
                <span className="bottom-tab-nav__indicator" aria-hidden="true" />
            </div>
        </nav>
    )
}
